import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { MdCancel, MdCheckCircle, MdRefresh, MdSchedule } from 'react-icons/md';
import { getMyProperties } from '../../../api/services/propertyService';
import { decideMoveOutRequest, getPropertyMoveOutRequests, issueEvictionNotice, recallEvictionNotice } from '../../../api/services/rentalService';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import { useMoveOutRequestFocus } from '../../../hooks/useMoveOutRequestFocus';
import './MoveOutRequests.css';

const STATUS_TABS = [
  { id: 'PENDING', label: 'Pending', icon: MdSchedule },
  { id: 'APPROVED', label: 'Accepted', icon: MdCheckCircle },
  { id: 'REJECTED', label: 'Rejected', icon: MdCancel },
  { id: 'EVICTIONS', label: 'Evictions', icon: MdCancel },
];

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatMonthYear = (month, year) => {
  const m = Number(month);
  const y = Number(year);
  if (!Number.isFinite(m) || !Number.isFinite(y)) return '—';
  const date = new Date(y, m - 1, 1);
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};

const isEvictionNotice = (row) => {
  const initiatedBy = String(row?.noticeInitiatedBy || '').toUpperCase();
  return initiatedBy === 'LANDLORD' || initiatedBy === 'AGENT';
};

const MoveOutRequests = ({ navigationState = {} }) => {
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('all');
  const [activeStatus, setActiveStatus] = useState('PENDING');
  const [rows, setRows] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [loadingRows, setLoadingRows] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [recallModal, setRecallModal] = useState({ open: false, assignmentId: null });
  const [decisionModal, setDecisionModal] = useState({
    open: false,
    mode: 'decision',
    row: null,
    decision: 'APPROVE',
    moveOutDate: '',
    reason: '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingProperties(true);
        const res = await getMyProperties({ page: 0, size: 200, search: '' });
        const content = res?.data?.content || [];
        const mapped = (Array.isArray(content) ? content : [])
          .map((p) => ({ id: p?.id, name: p?.name || 'Property' }))
          .filter((p) => p.id);
        if (cancelled) return;
        setProperties(mapped);
      } catch (e) {
        if (!cancelled) {
          setProperties([]);
          setError(e?.message || 'Could not load properties.');
        }
      } finally {
        if (!cancelled) setLoadingProperties(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (selectedPropertyId !== 'all') return;
    if (properties.length > 0) setSelectedPropertyId(properties[0].id);
  }, [properties, selectedPropertyId]);

  const loadRows = useCallback(async () => {
    if (loadingProperties) return;
    if (!properties.length) {
      setRows([]);
      setLoadingRows(false);
      return;
    }

    try {
      setLoadingRows(true);
      setError('');
      const targetPropertyIds =
        selectedPropertyId === 'all' ? properties.map((p) => p.id) : [selectedPropertyId].filter(Boolean);

      const responses = await Promise.all(
        targetPropertyIds.map((propertyId) =>
          getPropertyMoveOutRequests(propertyId, activeStatus === 'EVICTIONS' ? 'APPROVED' : activeStatus).then((res) =>
            Array.isArray(res?.data) ? res.data : []
          )
        )
      );

      const merged = [];
      const seen = new Set();
      responses.flat().forEach((row) => {
        if (!row?.id || seen.has(row.id)) return;
        seen.add(row.id);
        merged.push(row);
      });
      const normalized = activeStatus === 'EVICTIONS' ? merged.filter(isEvictionNotice) : merged.filter((row) => !isEvictionNotice(row));
      normalized.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
      setRows(normalized);
    } catch (e) {
      setRows([]);
      setError(e?.message || 'Could not load move-out requests.');
    } finally {
      setLoadingRows(false);
    }
  }, [activeStatus, loadingProperties, properties, selectedPropertyId]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const highlightedAssignmentId = useMoveOutRequestFocus(navigationState, {
    rows,
    loadingRows,
    loadingProperties,
    properties,
    setSelectedPropertyId,
    setActiveStatus,
  });

  const listTitle = useMemo(
    () => (
      activeStatus === 'PENDING'
        ? 'Pending move-out requests'
        : activeStatus === 'APPROVED'
          ? 'Accepted move-out requests'
          : activeStatus === 'REJECTED'
            ? 'Rejected move-out requests'
            : 'Issued eviction notices'
    ),
    [activeStatus]
  );

  const getInitialMoveOutDate = (row) => {
    let d = '';
    if (row?.noticeMoveOutDate) {
      d = String(row.noticeMoveOutDate).substring(0, 10);
    } else if (row?.noticeMoveOutYear && row?.noticeMoveOutMonth) {
      d = `${row.noticeMoveOutYear}-${String(row.noticeMoveOutMonth).padStart(2, '0')}-01`;
    } else {
      d = new Date().toISOString().substring(0, 10);
    }
    return d;
  };

  const openDecisionModal = (row, decision) => {
    setDecisionModal({
      open: true,
      mode: 'decision',
      row,
      decision,
      moveOutDate: getInitialMoveOutDate(row),
      reason: decision === 'APPROVE' ? 'Notice accepted' : '',
    });
  };

  const openEvictionModal = (row) => {
    setDecisionModal({
      open: true,
      mode: 'eviction',
      row,
      decision: 'REJECT',
      moveOutDate: new Date().toISOString().substring(0, 10),
      reason: '',
    });
  };

  const closeDecisionModal = () => {
    if (actionLoadingId) return;
    setDecisionModal({
      open: false,
      mode: 'decision',
      row: null,
      decision: 'APPROVE',
      moveOutDate: '',
      reason: '',
    });
  };

  const submitDecision = async () => {
    const row = decisionModal.row;
    if (!row) return;
    const assignmentId = row.assignmentId || row.id;
    if (!assignmentId) {
      setError('Missing assignment id for this request.');
      return;
    }
    if (!decisionModal.moveOutDate) {
      setError('Move-out date is required.');
      return;
    }
    if (decisionModal.mode === 'eviction' && !String(decisionModal.reason || '').trim()) {
      setError('Reason is required to issue an eviction notice.');
      return;
    }

    try {
      setActionLoadingId(assignmentId);
      setError('');
      if (decisionModal.mode === 'eviction') {
        await issueEvictionNotice(assignmentId, {
          moveOutDate: decisionModal.moveOutDate,
          reason: String(decisionModal.reason || '').trim(),
        });
      } else {
        await decideMoveOutRequest(assignmentId, {
          decision: decisionModal.decision,
          moveOutDate: decisionModal.moveOutDate,
          reason: decisionModal.reason || (decisionModal.decision === 'APPROVE' ? 'Notice accepted' : 'Notice rejected'),
        });
      }
      closeDecisionModal();
      await loadRows();
    } catch (e) {
      setError(e?.message || (decisionModal.mode === 'eviction' ? 'Could not issue eviction notice.' : 'Could not submit move-out decision.'));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleRecallEviction = async () => {
    if (!recallModal.assignmentId) return;
    try {
      setActionLoadingId(recallModal.assignmentId);
      setError('');
      await recallEvictionNotice(recallModal.assignmentId);
      closeRecallModal();
      await loadRows();
    } catch (e) {
      setError(e?.message || 'Could not recall eviction notice.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const closeRecallModal = () => {
    setRecallModal({ open: false, assignmentId: null });
  };

  return (
    <div className="move-out-requests-page">
      <CollapsibleTopPanel id="landlord-moveout-top" defaultOpenOnMobile={false}>
        <header className="move-out-requests-header">
          <div>
            <h1 className="move-out-requests-title">Move out requests</h1>
            <p className="move-out-requests-subtitle">Review tenant move-out notices by property and status.</p>
          </div>
          <button
            type="button"
            className="move-out-requests-refresh"
            onClick={loadRows}
            disabled={loadingRows || loadingProperties}
            aria-label="Refresh move out requests"
          >
            <MdRefresh className={loadingRows ? 'spin' : ''} />
          </button>
        </header>

        <div className="move-out-requests-toolbar">
          <div className="move-out-requests-field">
            <label htmlFor="mor-property">Property</label>
            <select
              id="mor-property"
              value={selectedPropertyId}
              onChange={(e) => setSelectedPropertyId(e.target.value)}
              disabled={loadingProperties || loadingRows || properties.length === 0}
            >
              <option value="all">All properties ({properties.length})</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </CollapsibleTopPanel>

      <div className="move-out-requests-tabs" role="tablist" aria-label="Move out status tabs">
        {STATUS_TABS.map((tab) => {
          const selected = activeStatus === tab.id;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              className={`move-out-requests-tab ${selected ? 'active' : ''}`}
              onClick={() => setActiveStatus(tab.id)}
            >
              <Icon />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      <h2 className="move-out-requests-list-title">{listTitle}</h2>

      {(loadingProperties || loadingRows) && <div className="move-out-requests-state">Loading requests...</div>}

      {!loadingProperties && !loadingRows && error ? (
        <div className="move-out-requests-state move-out-requests-state--error" role="alert">
          <p>{error}</p>
          <button type="button" onClick={loadRows}>
            Try again
          </button>
        </div>
      ) : null}

      {!loadingProperties && !loadingRows && !error && rows.length === 0 ? (
        <div className="move-out-requests-state">
          No{' '}
          {activeStatus === 'PENDING'
            ? 'pending'
            : activeStatus === 'APPROVED'
              ? 'accepted'
              : activeStatus === 'REJECTED'
                ? 'rejected'
                : 'issued eviction'}{' '}
          move-out requests found.
        </div>
      ) : null}

      {!loadingProperties && !loadingRows && !error && rows.length > 0 ? (
        <div className="move-out-requests-list">
          {rows.map((row) => {
            const assignmentId = String(row.assignmentId || row.id || '');
            const isHighlighted = assignmentId && assignmentId === highlightedAssignmentId;
            return (
            <article
              key={row.id}
              className={`move-out-request-card${isHighlighted ? ' move-out-request-card--highlighted' : ''}`}
              data-move-out-assignment-id={assignmentId || undefined}
            >
              <div className="move-out-request-card-top">
                <h3>{row.propertyName || 'Property'}</h3>
                <span className={`move-out-status move-out-status--${String(row.noticeStatus || '').toLowerCase()}`}>
                  {String(row.noticeStatus || '—').replace(/_/g, ' ')}
                </span>
              </div>
              <p className="move-out-request-unit">{row.unitName || row.unitNumber || 'Unit'}</p>
              <dl className="move-out-request-meta">
                <div>
                  <dt>Tenant</dt>
                  <dd>{[row.userFirstName, row.userLastName].filter(Boolean).join(' ') || row.userEmail || '—'}</dd>
                </div>
                <div>
                  <dt>Requested</dt>
                  <dd>{formatDateTime(row.createdAt)}</dd>
                </div>
                <div>
                  <dt>Planned move out</dt>
                  <dd>{formatMonthYear(row.noticeMoveOutMonth, row.noticeMoveOutYear)}</dd>
                </div>
                <div>
                  <dt>Reason</dt>
                  <dd>{row.noticeReason || 'No reason'}</dd>
                </div>
              </dl>
              {String(row.noticeStatus || '').toUpperCase() === 'PENDING' ? (
                <div className="move-out-request-actions">
                  <button
                    type="button"
                    className="move-out-action-btn move-out-action-btn--reject"
                    onClick={() => openDecisionModal(row, 'REJECT')}
                    disabled={Boolean(actionLoadingId)}
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    className="move-out-action-btn move-out-action-btn--evict"
                    onClick={() => openEvictionModal(row)}
                    disabled={Boolean(actionLoadingId)}
                  >
                    Issue Eviction
                  </button>
                  <button
                    type="button"
                    className="move-out-action-btn move-out-action-btn--approve"
                    onClick={() => openDecisionModal(row, 'APPROVE')}
                    disabled={Boolean(actionLoadingId)}
                  >
                    Approve
                  </button>
                </div>
              ) : activeStatus === 'EVICTIONS' || String(row.noticeStatus || '').toUpperCase() === 'EVICTION' ? (
                <div className="move-out-request-actions">
                  <button
                    type="button"
                    className="move-out-action-btn move-out-action-btn--reject"
                    onClick={() => setRecallModal({ open: true, assignmentId: row.assignmentId || row.id })}
                    disabled={Boolean(actionLoadingId)}
                  >
                    Cancel Eviction
                  </button>
                </div>
              ) : null}
            </article>
            );
          })}
        </div>
      ) : null}

      {decisionModal.open && decisionModal.row && typeof document !== 'undefined'
        ? createPortal(
          <div className="move-out-modal-overlay" role="dialog" aria-modal="true" aria-label="Move out decision">
            <button type="button" className="move-out-modal-backdrop" onClick={closeDecisionModal} />
            <div className="move-out-modal-panel">
              <h3>
                {decisionModal.mode === 'eviction'
                  ? 'Issue eviction notice'
                  : decisionModal.decision === 'APPROVE'
                    ? 'Approve move-out request'
                    : 'Reject move-out request'}
              </h3>
              <div className="move-out-modal-grid">
                <label>
                  Move-out Date
                  <input
                    type="date"
                    value={decisionModal.moveOutDate}
                    onChange={(e) => setDecisionModal((prev) => ({ ...prev, moveOutDate: e.target.value }))}
                  />
                </label>
              </div>
              <label className="move-out-modal-reason">
                Reason
                <textarea
                  rows={3}
                  value={decisionModal.reason}
                  onChange={(e) => setDecisionModal((prev) => ({ ...prev, reason: e.target.value }))}
                  placeholder="Write reason"
                />
              </label>
              <div className="move-out-modal-actions">
                <button type="button" className="move-out-action-btn move-out-action-btn--reject" onClick={closeDecisionModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={`move-out-action-btn ${decisionModal.mode === 'eviction' ? 'move-out-action-btn--evict' : 'move-out-action-btn--approve'
                    }`}
                  onClick={submitDecision}
                  disabled={Boolean(actionLoadingId)}
                >
                  {actionLoadingId
                    ? 'Submitting...'
                    : decisionModal.mode === 'eviction'
                      ? 'Issue Eviction'
                      : decisionModal.decision === 'APPROVE'
                        ? 'Approve'
                        : 'Reject'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
        : null}

      {recallModal.open && typeof document !== 'undefined' ? createPortal(
        <div className="modal-overlay" onClick={closeRecallModal}>
          <div className="modal-content" style={{ maxWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cancel Eviction Notice</h2>
              <button className="close-btn" onClick={closeRecallModal} disabled={Boolean(actionLoadingId)}>×</button>
            </div>
            <div className="modal-form">
              <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#4b5563', lineHeight: '1.5' }}>
                Are you sure you want to recall and cancel this eviction notice? The tenant will see that it has been canceled.
              </p>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeRecallModal} disabled={Boolean(actionLoadingId)}>
                  Keep Eviction
                </button>
                <button type="button" className="btn btn-danger" onClick={handleRecallEviction} disabled={Boolean(actionLoadingId)}>
                  {actionLoadingId === recallModal.assignmentId ? 'Canceling...' : 'Cancel Eviction'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      ) : null}
    </div>
  );
};

export default MoveOutRequests;
